import { environmentHttpClient } from "client/Api/httpClient";
import { SecretValue } from "client/Api/Apps/common";

export type GatewayModel = {
	id: number;
	name: string;
	gatewayUrl: string;
	serviceAccountUsername: string;
	serviceAccountPasswordContainsValue: boolean;
	versionId: number;
	isNewLaunchExperienceEnabled: boolean;
};

export type ConnectionModel = {
	gatewayId: number;
	id: string | null;
	name: string;
	serverAddress: string;
	port: string;
	enableBackground: boolean;
	program: string;
	workDirectory: string;
	parameters: string;
	terminalServerGatewayParameters: {
		hostName: string;
		port: string;
		domain: string;
		loadBalanceInfo: string;
	};
};

export type GatewayVersion = {
	id: number;
	name: string;
};

export type GatewayViewModel = {
	model: GatewayModel | null;
	isReadOnly: boolean;
	failedToLoadConnections: boolean;
	connections: ConnectionModel[];
	versions: GatewayVersion[];
};

export type GatewaySaveModel = {
	id: number | undefined;
	name: string;
	gatewayUrl: string;
	serviceAccountUsername: string;
	serviceAccountPassword: SecretValue;
	versionId: number;
	isNewLaunchExperienceEnabled: boolean;
};

export type ConnectionViewModel = {
	gatewayId: number;
	model: ConnectionModel | null;
	isReadOnly: boolean;
};

export function listGateways(signal?: AbortSignal): Promise<GatewayModel[]> {
	return environmentHttpClient.get<GatewayModel[]>(
		"UiApi/ClientlessRdp/Gateway/List",
		undefined,
		{ signal }
	);
}

export function getCreateGatewayViewModel(signal?: AbortSignal): Promise<GatewayViewModel> {
	return environmentHttpClient.get<GatewayViewModel>(
		"UiApi/ClientlessRdp/Gateway/New",
		undefined,
		{ signal }
	);
};

export function getViewGatewayViewModel(id: number, signal?: AbortSignal): Promise<GatewayViewModel> {
	return environmentHttpClient.get<GatewayViewModel>(
		`UiApi/ClientlessRdp/Gateway/${id}`,
		undefined,
		{ signal }
	);
}

export function getEditGatewayViewModel(id: number, signal?: AbortSignal): Promise<GatewayViewModel> {
	return environmentHttpClient.get<GatewayViewModel>(
		`UiApi/ClientlessRdp/Gateway/${id}/Edit`,
		undefined,
		{ signal }
	);
}

export function getCreateConnectionViewModel(gatewayId: number, signal?: AbortSignal): Promise<ConnectionViewModel> {
	return environmentHttpClient.get<ConnectionViewModel>(
		`UiApi/ClientlessRdp/Gateway/${gatewayId}/Connection/New`,
		undefined,
		{ signal }
	);
}

export function getViewConnectionViewModel(
	gatewayId: number,
	connectionId: string,
	signal?: AbortSignal
): Promise<ConnectionViewModel> {
	return environmentHttpClient.get<ConnectionViewModel>(
		`UiApi/ClientlessRdp/Gateway/${gatewayId}/Connection/${encodeURIComponent(connectionId)}`,
		undefined,
		{ signal }
	);
}

export function getEditConnectionViewModel(
	gatewayId: number,
	connectionId: string,
	signal?: AbortSignal
): Promise<ConnectionViewModel> {
	return environmentHttpClient.get<ConnectionViewModel>(
		`UiApi/ClientlessRdp/Gateway/${gatewayId}/Connection/${encodeURIComponent(connectionId)}/Edit`,
		undefined,
		{ signal }
	);
}

export function createGateway(model: GatewaySaveModel): Promise<number> {
	return environmentHttpClient.post<number>(
		"UiApi/ClientlessRdp/Gateway",
		model
	);
}

export function updateGateway(id: number, model: GatewaySaveModel): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/ClientlessRdp/Gateway/${id}`,
		model
	);
};

export function deleteGateway(id: number): Promise<void> {
	return environmentHttpClient.delete<void>(
		`UiApi/ClientlessRdp/Gateway/${id}`
	);
}

export function createConnection(gatewayId: number, model: ConnectionModel): Promise<void> {
	return environmentHttpClient.post<void>(
		`UiApi/ClientlessRdp/Gateway/${gatewayId}/Connection`,
		model
	);
};

export function updateConnection(
	gatewayId: number,
	id: string,
	model: ConnectionModel
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/ClientlessRdp/Gateway/${gatewayId}/Connection/${encodeURIComponent(id)}`,
		model
	);
};

export function deleteConnections(gatewayId: number, ids: readonly string[]): Promise<void> {
	return environmentHttpClient.delete<void>(
		`UiApi/ClientlessRdp/Gateway/${gatewayId}/Connection`,
		ids
	);
}
